/**
 * Hook for claiming all available USDv (completing unstake after cooldown).
 *
 * The amount to claim is read directly from chain at submit time and passed
 * to the SDK as a raw bigint — the frontend never multiplies, divides, or
 * sums token amounts. This avoids any chance of a precision/rounding drift
 * between what the UI computes and what the on-chain `unstake` instruction
 * actually accepts.
 */
import { notifyTransaction, type TransactionObserver } from "@shared/types/transactionObserver";
import {
    confirmTransaction,
    fromRawAmount,
    INITIAL_TRANSACTION_STATE,
    TransactionError,
    type TransactionState,
    type TransactionStep,
    useTransactionSigner,
    useWallet
} from "@solomon-apps/shared";
import { DEFAULT_SALT, TOKEN_DECIMALS } from "@solomon-apps/shared";
import type { BigDecimal } from "@solomon-labs/bigdecimal";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { SolomonStake } from "@solomon-labs/solomon-protocol";
import type { SolanaTransactionSignature } from "@solomon-labs/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

/**
 * Result of a successful claimAll.
 */
export interface ClaimAllResult {
    signature: string;
    /**
     * The exact amount claimed, as the chain saw it (BigDecimal in USDv units).
     * Use this for optimistic local-state updates so they match the on-chain delta.
     */
    assetsClaimed: BigDecimal;
}

/**
 * Return type for useUnstakeTransaction hook
 */
export interface UseUnstakeTransactionReturn {
    /** Current transaction state */
    state: TransactionState;
    /** Claim all available assets. Reads the exact claimable amount from chain. */
    claimAll: () => Promise<ClaimAllResult | null>;
    /** Reset state to initial */
    reset: () => void;
}

export function useUnstakeTransaction(
    salt: Uint8Array = DEFAULT_SALT,
    observer?: TransactionObserver
): UseUnstakeTransactionReturn {
    const { connected, publicKey } = useWallet();
    const { signer, ready: signerReady } = useTransactionSigner();
    const queryClient = useQueryClient();
    const [state, setState] = useState<TransactionState>(INITIAL_TRANSACTION_STATE);
    const isExecutingRef = useRef(false);

    const claimAll = useCallback(async (): Promise<ClaimAllResult | null> => {
        if (isExecutingRef.current) return null;
        isExecutingRef.current = true;

        let currentStep: TransactionStep = "building";
        let confirmationUnknown = false;
        notifyTransaction(() => observer?.stage?.("building"));
        try {
            // Validation
            if (!connected || !publicKey) {
                const error = TransactionError.validation("WALLET_NOT_CONNECTED", "Wallet not connected");
                setState({
                    ...INITIAL_TRANSACTION_STATE,
                    isError: true,
                    errorMessage: error.userMessage
                });
                notifyTransaction(() => observer?.failed?.(error, false));
                return null;
            }

            if (!signerReady || !signer) {
                const error = TransactionError.validation("SIGNER_NOT_READY", "Transaction signer not ready");
                setState({
                    ...INITIAL_TRANSACTION_STATE,
                    isError: true,
                    errorMessage: error.userMessage
                });
                notifyTransaction(() => observer?.failed?.(error, false));
                return null;
            }

            setState({
                ...INITIAL_TRANSACTION_STATE,
                isLoading: true,
                step: "building"
            });

            // Read the exact claimable amount from chain. The SDK helper sums
            // `assetsAvailable` + matured queue entries server-side; the bigint
            // it returns is what we hand straight to the unstake instruction —
            // no client-side arithmetic.
            const availableAssets = await SolomonStake.getAvailableAssetsForUser(publicKey, salt);

            if (availableAssets === 0n) {
                const error = TransactionError.validation("INVALID_AMOUNT", "No assets available to claim");
                setState({
                    ...INITIAL_TRANSACTION_STATE,
                    isError: true,
                    errorMessage: error.userMessage
                });
                notifyTransaction(() => observer?.failed?.(error, false));
                return null;
            }

            // Get recent blockhash
            const blockhashResponse = await ConnectionManager.getRpcClient().getLatestBlockhash();
            if (!blockhashResponse) {
                throw new Error("Failed to get recent blockhash");
            }
            const { blockhash: recentBlockhash, lastValidBlockHeight } = blockhashResponse;

            // Build unstake (claim) transaction with the raw bigint amount.
            const transaction = await SolomonStake.createUnstakeTransaction(publicKey, salt, availableAssets, {
                feePayer: publicKey,
                recentBlockhash
            });

            currentStep = "signing";
            notifyTransaction(() => observer?.stage?.("awaiting_signature"));
            setState((s) => ({ ...s, step: "signing" }));

            const serializedTx = transaction.serialize();
            const signature = await signer.signAndSendTransaction(serializedTx);

            currentStep = "confirming";
            notifyTransaction(() => observer?.submitted?.(signature));
            notifyTransaction(() => observer?.stage?.("confirming"));
            setState((s) => ({
                ...s,
                step: "confirming",
                signature
            }));

            const result = await confirmTransaction(signature as SolanaTransactionSignature, {
                lastValidBlockHeight
            });
            if (!result.confirmed) {
                confirmationUnknown = !/transaction (failed|expired)/i.test(result.error ?? "");
                throw new Error(result.error || "Transaction failed to confirm");
            }

            setState({
                isLoading: false,
                isSuccess: true,
                isError: false,
                errorMessage: null,
                signature,
                step: "complete"
            });

            void queryClient.invalidateQueries({ queryKey: ["user-staking-stats"] });
            void queryClient.invalidateQueries({ queryKey: ["staking-stats"] });
            void queryClient.invalidateQueries({ queryKey: ["protocol-data"] });
            void queryClient.invalidateQueries({ queryKey: ["transactionHistory"] });

            return {
                signature,
                assetsClaimed: fromRawAmount(availableAssets, TOKEN_DECIMALS.USDv)
            };
        } catch (error) {
            notifyTransaction(() =>
                observer?.failed?.(
                    error,
                    confirmationUnknown ||
                        (currentStep === "signing" &&
                            !/reject|denied|cancel/i.test(error instanceof Error ? error.message : ""))
                )
            );
            console.error(`[useUnstakeTransaction] Failed at step "${currentStep}":`, error);
            const txError = TransactionError.fromCaught(error, currentStep);
            setState({
                isLoading: false,
                isSuccess: false,
                isError: true,
                errorMessage: txError.userMessage,
                signature: null,
                step: "idle"
            });
            return null;
        } finally {
            isExecutingRef.current = false;
        }
    }, [connected, publicKey, signer, signerReady, salt, queryClient, observer]);

    const reset = useCallback(() => {
        setState(INITIAL_TRANSACTION_STATE);
    }, []);

    return { state, claimAll, reset };
}
